import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useState,
} from "react";

// A step that's still loading holds the wizard (BRK-S492; Nav, 2026-10-28: "I
// was just able to click on the primary while the screen was loading"). The
// base wizard provides the hold around the open step and disables its primary
// button (Next, Save and return, the last page's) while any part of the step
// holds it, so a press never checks a half-read step. Back stays. Every
// `StepLoading` holds it while it's there; a step whose reads show no
// placeholder, such as the last step sending the application to each carrier,
// holds it itself.

export const StepHoldContext = createContext<(() => () => void) | null>(null);

/** Holds the open step while `active`. Outside a wizard it does nothing. */
export function useStepHold(active: boolean): void {
  const start = useContext(StepHoldContext);
  // A layout effect, not a passive one (BRK-S514): its state update re-renders
  // the wizard before the commit that drew the placeholder reaches the screen,
  // so the button is never enabled under it. A passive effect ran a frame
  // later, and a press could land on the enabled button in between.
  useLayoutEffect(() => {
    if (!active || !start) return;
    return start();
  }, [active, start]);
}

/** The wizard's side: whether anything holds the step, and how to hold it. */
export function useStepHolds(): { held: boolean; hold: () => () => void } {
  const [holding, setHolding] = useState(0);
  const hold = useCallback(() => {
    setHolding((n) => n + 1);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      setHolding((n) => n - 1);
    };
  }, []);
  return { held: holding > 0, hold };
}
